import axios, { AxiosInstance, AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { env } from '@/utils/constants/env';
import axiosRetry from 'axios-retry';
import useAuthState from '@/states/auth.state';
import useTenantState from '@/states/tenant.state';
import useTrialState from '@/states/trial.state';

const axiosInstance = axios.create({
  baseURL: env.apiTasklistBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - set token and tenant headers
axiosInstance.interceptors.request.use(
  async (config) => {
    try {
      // Get fresh token (will refresh if expired)
      const token = await useAuthState.getState().getValidToken();
      const activeTenant = useTenantState.getState().currentTenant;

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      if (activeTenant) {
        config.headers['X-ACTIVE-TENANT'] = activeTenant.slug;
      }

      // Only set Content-Type to JSON if it's not FormData
      // FormData should let axios handle Content-Type automatically with boundary
      if (config.data instanceof FormData) {
        // Delete any existing Content-Type to let axios set it automatically
        console.log('🔴 Detected FormData, removing Content-Type header');
        delete config.headers['Content-Type'];
      } else {
        console.log('🟢 Non-FormData request, setting Content-Type to application/json');
        config.headers['Content-Type'] = 'application/json';
      }

      return config;
    } catch (error) {
      console.error('Error in request interceptor:', error);
      return config;
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);
const checkTrialExpiration = (error: any) => {
  const restrictedPrefixes = [
    '/integration',
    '/master-data',
    '/external-page',
    '/external-service',
    '/migration',
    '/organization-structure',
    '/question',
    '/dashboard'
  ];

  const currentPath = window.location.pathname;
  const isRestrictedPath = restrictedPrefixes.some(prefix => 
    currentPath.includes(prefix)
  );

  // Check if it's a 403 error on a restricted path
  const is403Error = error.response?.status === 403;
  
  // Check for specific trial expired error OR any 403 on restricted path
  const isTrialExpiredError = 
    is403Error && (
      error.response?.data?.error === 'TRIAL_EXPIRED_BLOCKED' ||
      error.response?.data?.message === 'TRIAL_EXPIRED_BLOCKED' ||
      error.response?.data?.error === 'Trial has expired'
    );

  // Show restricted page if: trial expired error, OR 403 on restricted path
  if ((isTrialExpiredError || is403Error) && isRestrictedPath) {
    const { keycloakUserInfo } = useAuthState.getState();
    const trialEndsAt = keycloakUserInfo?.trial_ends_at || null;
    
    // Determine feature name from path
    const featureMap: Record<string, string> = {
      '/integration': 'Integration features',
      '/master-data': 'Master Data features',
      '/external-page': 'External Page features',
      '/external-service': 'External Service features',
      '/migration': 'Migration features',
      '/organization-structure': 'Organization Structure features',
      '/question': 'Question features',
      '/dashboard': 'Dashboard features'
    };
    
    const featureName = Object.entries(featureMap).find(([prefix]) => 
      currentPath.includes(prefix)
    )?.[1] || 'these features';
    
    // Trigger the restricted access page via state
    useTrialState.getState().setTrialRestricted(true, featureName, trialEndsAt);

    // Mark error as handled so downstream handlers can ignore it
    error.isTrialExpiredHandled = true;
    return Promise.reject(error);
  }
};

// Response interceptor - handle 401 errors with token refresh
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Check for trial expiration
    checkTrialExpiration(error);

    // Check if error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Get a valid token (keycloak-js handles refresh internally)
        const newToken = await useAuthState.getState().getValidToken();
        
        if (newToken) {
          // Update the authorization header and retry the original request
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        } else {
          // No valid token available, redirect to login
          console.warn('No valid token available after refresh attempt');
          globalThis.location.href = '/login';
          throw new Error('Authentication required');
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // Redirect to login will be handled by the auth state
        throw refreshError;
      }
    }

    throw error;
  }
);

// Retry configuration for network errors
axiosRetry(axiosInstance, {
  retries: 2,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) => {
    // Retry on network errors and 5xx status codes, but not on 401 (handled by interceptor)
    return axiosRetry.isNetworkOrIdempotentRequestError(error) || 
           (error.response?.status !== undefined && error.response.status >= 500 && error.response.status < 600);
  },
});

export { axiosInstance };

// Helper function to apply auth interceptors to any axios instance
const applyAuthInterceptors = (instance: AxiosInstance) => {
  // Request interceptor
  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      try {
        const token = await useAuthState.getState().getValidToken();
        const activeTenant = useTenantState.getState().currentTenant;

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        if (activeTenant) {
          config.headers['X-ACTIVE-TENANT'] = activeTenant.slug;
        }

        // Only set Content-Type to JSON if it's not FormData
        // FormData should let axios handle Content-Type automatically with boundary
        if (config.data instanceof FormData) {
          // Delete any existing Content-Type to let axios set it automatically
          delete config.headers['Content-Type'];
        } else {
          config.headers['Content-Type'] = 'application/json';
        }

        return config;
      } catch (error) {
        console.error('Error in request interceptor:', error);
        return config;
      }
    },
    (error: AxiosError) => {
      throw error;
    }
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Check for trial expiration
      checkTrialExpiration(error);

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          // Get a valid token (keycloak-js handles refresh internally)
          const newToken = await useAuthState.getState().getValidToken();
          
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return instance(originalRequest);
          } else {
            // No valid token available, redirect to login
            console.warn('No valid token available after refresh attempt');
            globalThis.location.href = '/login';
            throw new Error('Authentication required');
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          throw refreshError;
        }
      }

      throw error;
    }
  );
};

// Create specialized axios instances for different services
export const createTasklistAxiosInstance = () => {
  const tasklistInstance = axios.create({
    baseURL: env.apiTasklistBaseUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Apply auth interceptors
  applyAuthInterceptors(tasklistInstance);

  // Apply retry configuration
  axiosRetry(tasklistInstance, {
    retries: 2,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: (error) => {
      return axiosRetry.isNetworkOrIdempotentRequestError(error) || 
             (error.response?.status !== undefined && error.response.status >= 500 && error.response.status < 600);
    },
  });

  return tasklistInstance;
};

// Export ready-to-use instances
export const tasklistAxios = createTasklistAxiosInstance();
